import { SimpleRelease } from '@common/types/release/simple-release';
import { ButtonType } from '@components/actions/button';
import Typography from '@components/basics/typography';
import BoxHeader from '@components/layout/box-header';
import MotionOpacity from '@components/motion/motion-opacity';
import { MerchProduct } from '@services/merch/types/api/merch-product';
import MenuDropdown from '@webapp/controllers/form/menu-dropdown';
import { CreateMerchProduct } from '@webapp/controllers/merch/create-merch-product';
import MerchProductDisplayListCtrl from '@webapp/controllers/merch/merch-product-display-list';
import {
  CleanMerchStore,
  DeleteMerch,
  ListArtistMerch,
  PublishProduct,
} from '@webapp/store/merch/action-creators';
import { Merch } from '@webapp/store/merch/state';
import { ListReleases } from '@webapp/store/release/action-creators';
import AppState from '@webapp/store/state';
import { CreateDialog } from '@webapp/store/ui/action-creators';
import { Option } from '@webapp/types/common/option';
import { DialogConfiguration } from '@webapp/types/ui/dialog-configuration';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import * as React from 'react';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { Redirect } from 'react-router-dom';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { LIST_ARTIST_MERCH } from '@webapp/store/merch/actions';
import { LIST_SUBSCRIPTIONS } from '@webapp/store/subscription-manager/actions';
import { hasMerchSubscription } from '@webapp/store/subscription/selectors';
import { merchReleaseInitiatedEvent } from '@webapp/utils/rudderstack/events';

interface ArtistMerchPageProps {
  className?: string;
  artistId?: string;
  artistName?: string;
  active?: Partial<Merch>;
  goToMerch: () => CallHistoryMethodAction;
  openModal: (
    merchProductId: string,
    titleKey: string,
    textKey: string,
    callback: (value: any) => void
  ) => CreateDialog;
  publish: (artistId: string, productId: string) => PublishProduct;
  takeDown: (artistId: string, productId: string) => DeleteMerch;
  listArtistMerch: (artistId: string) => ListArtistMerch;
  cleanMerchStore: () => CleanMerchStore;
  products: MerchProduct[];
  releases: SimpleRelease[];
  merchEnabled: boolean;
  isLoading: boolean;
  loaded: boolean;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goToMerch: () => dispatch(push('/merch')),
  openModal: (
    merchProductId: string,
    titleKey: string,
    textKey: string,
    callback: (value: any) => void
  ) =>
    dispatch(
      new CreateDialog(merchProductId, buildDialog(titleKey, textKey, callback))
    ),
  publish: (artistId: string, productId: string) =>
    dispatch(new PublishProduct(artistId, productId)),
  takeDown: (artistId: string, productId: string) =>
    dispatch(new DeleteMerch(artistId, productId)),
  listArtistMerch: (artistId: string) =>
    dispatch(new ListArtistMerch(artistId)),
  cleanMerchStore: () => dispatch(new CleanMerchStore()),
  listReleases: () => dispatch(new ListReleases('all')),
});

const mapStateToProps = (state: AppState) => ({
  artistId: state.artist.active?.id,
  artistName: state.artist.active?.name,
  active: state.merch.active,
  products: state.merch.artistMerch || [],
  releases: state.release.list.all || [],
  merchEnabled: hasMerchSubscription(state),
  isLoading: !!(
    state.ui.loading[LIST_ARTIST_MERCH] || state.ui.loading[LIST_SUBSCRIPTIONS]
  ),
  loaded: state.subscriptionManager.loaded && state.merch.loaded,
});

const buildDialog = (
  titleKey: string,
  textKey: string,
  callback: (value: any) => void
): DialogConfiguration<ButtonType> => {
  return {
    title: titleKey ? <Translate id={titleKey} /> : null,
    text: textKey ? <Translate id={textKey} /> : null,
    actions: [
      { type: 'primary', label: 'No', value: false },
      { type: 'secondary', label: 'Yes', value: true },
    ],
    callback,
  };
};

export const ArtistMerchPage: React.FC<ArtistMerchPageProps> = ({
  className,
  goToMerch,
  openModal,
  publish,
  takeDown,
  listArtistMerch,
  cleanMerchStore,
  artistId,
  artistName,
  products,
  isLoading,
  loaded,
  merchEnabled,
}) => {
  const DROPDOWN_DATA = [
    {
      label: `Published`,
      value: 'published',
    },
    {
      label: `Drafts`,
      value: 'draft',
    },
  ];

  const query = new URLSearchParams(useLocation().search);

  const [activeTab, setActiveTab] = React.useState(
    query.has('draft') || query.has('drafts') ? 'draft' : 'published'
  );
  const [activeReleaseId, setActiveReleaseId] = React.useState<string>();

  React.useEffect(() => {
    cleanMerchStore();
    if (artistId) {
      listArtistMerch(artistId);
    }
  }, [artistId, listArtistMerch, cleanMerchStore]);

  const handleTakeDown = (product: MerchProduct) => {
    if (artistId && product.id) {
      openModal(
        product.id,
        'ARTIST_SITE.MERCH.MODAL.TAKEDOWN.TITLE',
        'ARTIST_SITE.MERCH.MODAL.TAKEDOWN.TEXT',
        (confirmation) => {
          if (confirmation) {
            takeDown(artistId, product.id);
          }
        }
      );
    }
  };

  const handlePublish = (product: MerchProduct) => {
    if (artistId && product.id) {
      openModal(
        product.id,
        'ARTIST_SITE.MERCH.MODAL.PUBLISH.TITLE',
        'ARTIST_SITE.MERCH.MODAL.PUBLISH.TEXT',
        (confirmation) => {
          if (confirmation) {
            publish(artistId, product.id);
          }
        }
      );
    }
  };

  const filteredProducts = (products || [])
    .filter((prod) => prod.status === activeTab)
    .filter((prod) =>
      activeReleaseId ? prod.releaseId === activeReleaseId : true
    )
    .sort((a, b) => {
      let result = (a.order || 0) - (b.order || 0);
      if (result === 0 && a.status === b.status) {
        result =
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return result;
    });

  const createMerchProduct = () => {
    return merchReleaseInitiatedEvent({
      artist_id: artistId,
      artist_name: artistName,
    }).then(() => {
      return goToMerch();
    });
  };

  if (isLoading || !loaded) {
    return null;
  }

  if (!merchEnabled) {
    return <Redirect to="/merch" />;
  }

  if (!products.length) {
    return <Redirect to="/merch/welcome" />;
  }

  return (
    <MotionOpacity className={className}>
      <BoxHeader
        className="mb-30"
        title={<Translate id="ARTIST_SITE.MERCH.TITLE" />}
        text={<Translate id="ARTIST_SITE.MERCH.TEXT" />}
        customAction={
          <StyledDropdown
            placeholder={
              DROPDOWN_DATA.find((tab) => tab.value === activeTab)?.label ?? ''
            }
            menuContent={DROPDOWN_DATA as Option[]}
            active={activeTab}
            isSmall
            onSelect={(value) => {
              setActiveTab(value);
              setActiveReleaseId(undefined);
            }}
          />
        }
      />
      {products && products.length > 0 && (
        <>
          <CreateMerchProduct goToMerch={createMerchProduct} />
          <MerchProductDisplayListCtrl
            handleTakeDown={handleTakeDown}
            handlePublish={handlePublish}
            products={filteredProducts}
            takeDown={takeDown}
          />{' '}
        </>
      )}
      {filteredProducts && !filteredProducts.length && (
        <Typography className="text" opacity=".5" weight="400" type="title">
          <Translate id="ARTIST_SITE.MERCH.NO_PRODUCTS" />
        </Typography>
      )}
    </MotionOpacity>
  );
};

const StyledDropdown = styled(MenuDropdown)`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.dropdownBG};
  padding: 10px 20px;
  .placeholder {
    margin-right: 30px;
    padding-top: 7px;
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
    color: ${({ theme }) => theme.colors.pureBlackWhite};
    opacity: 1 !important;
  }
`;
export default connect(mapStateToProps, mapDispatchToProps)(ArtistMerchPage);
